// FormTemplatesManager.jsx - Manage bot templates per Lead Form
// User can add a template per Facebook Lead Form so the bot greets accordingly

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export default function FormTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await supabase.from("lead_form_templates").select("*").order("created_at", { ascending: false });
    setTemplates(data || []);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await supabase.from("lead_form_templates").delete().eq("id", id);
    load();
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S}}>📋 Form Templates</div>
          <div style={{fontSize:11,color:C.muted}}>Different bot message per Lead Form (e.g. "Mother's Day Botox" gets different SMS than "Membership Inquiry")</div>
        </div>
        <button onClick={()=>setShowNew(true)} style={{padding:"8px 14px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ New Template</button>
      </div>

      {templates.length === 0 ? (
        <div style={{padding:30,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
          <div style={{fontSize:32,marginBottom:8}}>📋</div>
          <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>No form templates yet</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Add a template per Lead Form so the bot sends different messages for each campaign</div>
          <button onClick={()=>setShowNew(true)} style={{padding:"8px 14px",background:C.gold,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Create First Template</button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {templates.map(t => (
            <div key={t.id} style={{padding:14,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:3}}>{t.form_name}</div>
                  <div style={{fontSize:11,color:C.muted}}>
                    {t.default_interest && <>Interest: {t.default_interest} · </>}
                    {t.estimated_value > 0 && <>Est: ${t.estimated_value} · </>}
                    {t.total_leads_received || 0} leads · {t.total_booked || 0} booked
                  </div>
                  {t.form_id && <div style={{fontSize:10,color:C.dim,fontFamily:"monospace",marginTop:3}}>Form ID: {t.form_id}</div>}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setEditing(t)} style={{padding:"5px 10px",background:C.gold,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>✏️ Edit</button>
                  <button onClick={()=>remove(t.id)} style={{padding:"5px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>🗑</button>
                </div>
              </div>
              {t.welcome_sms_template && (
                <div style={{padding:10,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,color:C.text,whiteSpace:"pre-wrap"}}>
                  💬 {t.welcome_sms_template.substring(0,200)}{t.welcome_sms_template.length > 200 ? "..." : ""}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(editing || showNew) && (
        <FormTemplateEditor
          template={editing}
          onClose={()=>{setEditing(null); setShowNew(false); load();}}
        />
      )}
    </div>
  );
}

function FormTemplateEditor({ template, onClose }) {
  const isNew = !template;
  const [form, setForm] = useState(template || {
    form_id: "",
    form_name: "",
    ad_id: "",
    ad_name: "",
    default_interest: "",
    welcome_sms_template: "Hi {{first_name}}! 👋 Thanks for reaching out about {{interest}} at Nova Med Spa. I'd love to find a time that works for you - what's your availability this week?\n\nBook here: {{book_link}}\nOr just reply to this message!",
    welcome_email_subject: "Welcome to Nova Med Spa, {{first_name}} ✨",
    welcome_email_template: "",
    bot_can_book: true,
    bot_handoff_after_minutes: 5,
    estimated_value: 0,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    if (!form.form_name?.trim()) { alert("Name required"); setSaving(false); return; }

    if (isNew) {
      await supabase.from("lead_form_templates").insert([form]);
    } else {
      await supabase.from("lead_form_templates").update(form).eq("id", template.id);
    }
    setSaving(false);
    onClose();
  };

  const update = (k, v) => setForm(f => ({...f, [k]: v}));

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"92vh",overflow:"auto"}}>
        <div style={{padding:"16px 22px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
          <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>{isNew ? "+ New Form Template" : "✏️ Edit Template"}</div>
        </div>

        <div style={{padding:22}}>
          <Field label="Template Name *">
            <input value={form.form_name} onChange={e=>update("form_name", e.target.value)} placeholder="e.g. Mother's Day Botox Promo" style={inp}/>
          </Field>

          <Field label="Facebook Form ID (find in Make.com run history or Facebook Ads Manager)">
            <input value={form.form_id || ""} onChange={e=>update("form_id", e.target.value)} placeholder="e.g. 1234567890123456" style={{...inp,fontFamily:"monospace",fontSize:12}}/>
          </Field>

          <Field label="Default Interest (overrides if not in form)">
            <input value={form.default_interest || ""} onChange={e=>update("default_interest", e.target.value)} placeholder="Botox / Filler / Membership / etc." style={inp}/>
          </Field>

          <Field label="Estimated Value ($)">
            <input type="number" value={form.estimated_value || 0} onChange={e=>update("estimated_value", +e.target.value)} placeholder="450" style={inp}/>
          </Field>

          <div style={{marginTop:14,padding:12,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,marginBottom:6,textTransform:"uppercase"}}>💬 Welcome SMS Template</div>
            <div style={{fontSize:10,color:C.muted,marginBottom:6}}>Use {`{{first_name}}`} {`{{interest}}`} {`{{book_link}}`}</div>
            <textarea value={form.welcome_sms_template || ""} onChange={e=>update("welcome_sms_template", e.target.value)} rows={5} style={{...inp,resize:"vertical",fontFamily:"monospace",fontSize:12}}/>
          </div>

          <Field label="📧 Welcome Email Subject">
            <input value={form.welcome_email_subject || ""} onChange={e=>update("welcome_email_subject", e.target.value)} style={inp}/>
          </Field>

          <Field label="🤖 Bot can attempt booking">
            <select value={form.bot_can_book ? "yes" : "no"} onChange={e=>update("bot_can_book", e.target.value === "yes")} style={inp}>
              <option value="yes">Yes - bot can book</option>
              <option value="no">No - hand to human immediately</option>
            </select>
          </Field>

          <Field label="⏰ Hand off to human after (minutes)">
            <input type="number" value={form.bot_handoff_after_minutes || 5} onChange={e=>update("bot_handoff_after_minutes", +e.target.value)} style={inp}/>
          </Field>

          <Field label="Active">
            <button onClick={()=>update("is_active", !form.is_active)} style={{padding:"8px 14px",background:form.is_active?C.greenL:C.light,color:form.is_active?C.green:C.muted,border:"1px solid "+(form.is_active?C.green+"40":C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>
              {form.is_active ? "✅ Active" : "⏸ Inactive"}
            </button>
          </Field>

          <div style={{display:"flex",gap:8,marginTop:18}}>
            <button onClick={onClose} style={{flex:1,padding:"11px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={save} disabled={saving} style={{flex:2,padding:"11px",background:saving?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:saving?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F}}>
              {saving ? "Saving..." : "💾 Save Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{label}</label>
      {children}
    </div>
  );
}
